import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Info, Circle, CheckCircle2, QrCode } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, apiError } from "@/lib/api";
import { VerifiedBadge } from "@/components/fleet/RideCard";
import { DocumentPreview } from "@/components/fleet/DocumentPreview";
import { DocCheckResult } from "@/components/fleet/DocCheckResult";
import { PayoutCard } from "@/components/fleet/PayoutCard";
import { RatingSummary } from "@/components/fleet/Stars";

import { t } from "@/lib/i18n";

const Field = ({ label, testid, value, onChange, placeholder, required = true }) => (
  <label className="block">
    <span className="ff-label">{t(label)}</span>
    <input required={required} minLength={required ? 2 : undefined} data-testid={testid} className="ff-field" placeholder={placeholder && t(placeholder)} value={value} onChange={(e) => onChange(e.target.value)} />
  </label>
);

const Check = ({ ok, label }) => (
  <li className={`flex items-center gap-2 text-sm ${ok ? "text-white" : "text-[#94A3B8]"}`}>
    {ok ? <CheckCircle2 size={17} className="text-[#10B981]" /> : <Circle size={17} />} {label}
  </li>
);

const fromProfile = (p) => ({ display_name: p.display_name, license_number: p.license_number, license_city: p.license_city, phone: p.phone || "", home_cities: (p.home_cities || []).join(", ") });

export const ProfileSheet = ({ open, onClose, profile, onSaved, onVerifyInfo, onConcierge, onLogout }) => {
  const [form, setForm] = useState({ display_name: "", license_number: "", license_city: "", phone: "", home_cities: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const p = profile || {};

  useEffect(() => {
    if (open && profile) setForm(fromProfile(profile));
  }, [open, profile]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const home_cities = form.home_cities.split(",").map((c) => c.trim()).filter(Boolean);
      const { data } = await api.put("/profile", { ...form, home_cities });
      toast.success(data.verified ? "Profilo verificato • NCC Verificato 🛡️" : "Dati salvati");
      onSaved();
    } catch (err) {
      toast.error(apiError(err, "Salvataggio non riuscito"));
    } finally {
      setSaving(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("File troppo grande (max 10 MB)");
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/profile/document", fd, { timeout: 120000 });
      const st = data.doc_check?.status;
      if (st === "recognized") toast.success(data.verified ? "Documento riconosciuto • NCC Verificato 🛡️" : "Documento riconosciuto • completa i campi mancanti");
      else toast.error(data.doc_check?.reason || "Documento non riconosciuto");
      onSaved();
    } catch (err) {
      toast.error(apiError(err, "Caricamento non riuscito"));
    } finally {
      setUploading(false);
    }
  };

  const recognized = p.doc_check?.status === "recognized";

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="profile-sheet" className="ff-glass-panel w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="text-left">
          <SheetTitle className="ff-display text-2xl font-extrabold text-white">{t("Profilo")}</SheetTitle>
          <SheetDescription className="text-[#94A3B8]">{t("Puoi già pubblicare e prenotare corse. Carica la licenza (facoltativo) per ottenere il badge NCC Verificato.")}</SheetDescription>
        </SheetHeader>
        <div className="mt-6 ff-card p-4 flex items-center justify-between gap-3">
          <span data-testid="profile-name" className="text-lg font-bold text-white truncate">{p.display_name || "Profilo da completare"}</span>
          {p.verified ? <VerifiedBadge testid="profile-verified-badge" /> : <span data-testid="profile-unverified-badge" className="ff-badge ff-badge-muted text-xs">Non verificato</span>}
        </div>
        <div className="mt-3"><RatingSummary rating={p.rating} /></div>
        <span className="ff-label mt-4 !mb-0 block">{t("Per il badge NCC Verificato (facoltativo)")}</span>
        <ul className="mt-2 flex flex-col gap-2" data-testid="profile-checklist">
          <Check ok={recognized} label="Foto Licenza NCC / Libretto riconosciuta" />
          <Check ok={!!p.display_name} label="Nome / Ragione sociale" />
          <Check ok={!!p.license_number} label="Numero Licenza NCC / Autorizzazione" />
          <Check ok={!!p.license_city} label="Comune di Rilascio" />
        </ul>
        <div className="mt-6 flex flex-col gap-3">
          <span className="ff-label !mb-0">1. Foto Licenza NCC / Libretto del Veicolo (facoltativo)</span>
          <p className="text-xs text-[#94A3B8] -mt-1">Lettura automatica dei dati e assegnazione del badge. JPG, PNG, WEBP, PDF • max 10 MB.</p>
          {p.has_document && <DocumentPreview profile={p} />}
          <DocCheckResult check={p.doc_check} />
          <label data-testid="profile-document-upload-label" className={`ff-btn-primary w-full text-sm uppercase cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
            <Upload size={18} /> {uploading ? "Verifica automatica in corso…" : p.has_document ? "Carica un altro documento" : "Fotografa o carica documento"}
            <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" data-testid="profile-document-input" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
        </div>
        <form onSubmit={save} className="mt-6 flex flex-col gap-4" data-testid="profile-form">
          <span className="ff-label !mb-0">2. Dati profilo (licenza compilata in automatico se leggibile)</span>
          <Field label="Nome e Cognome / Ragione sociale" testid="profile-display-name-input" value={form.display_name} onChange={set("display_name")} placeholder="Es. Mario Rossi NCC" />
          <Field label="Numero Licenza NCC / Autorizzazione" testid="profile-license-number-input" required={false} value={form.license_number} onChange={set("license_number")} placeholder="Es. NCC-12345" />
          <Field label="Comune di Rilascio" testid="profile-license-city-input" required={false} value={form.license_city} onChange={set("license_city")} placeholder="Es. Firenze" />
          <Field label="Telefono diretto (visibile solo al collega di una corsa confermata)" testid="profile-phone-input" required={false} value={form.phone} onChange={set("phone")} placeholder="Es. +39 333 1234567" />
          <Field label="Smart Match • Città o rotte abituali (separate da virgola)" testid="profile-home-cities-input" required={false} value={form.home_cities} onChange={set("home_cities")} placeholder="Es. Firenze, Pisa, Milano" />
          <button type="submit" disabled={saving} data-testid="profile-save-button" className="ff-btn-secondary w-full uppercase text-base">{saving ? t("Salvataggio…") : t("Salva dati")}</button>
        </form>
        <PayoutCard profile={p} onSaved={onSaved} />
        <button type="button" data-testid="profile-concierge-button" onClick={onConcierge} className="ff-btn-secondary w-full mt-6 text-sm uppercase border-[#3B82F6]">
          <QrCode size={18} /> {t("Scarica QR Code Concierge Hotel")}
        </button>
        <button type="button" data-testid="profile-verify-info-button" onClick={onVerifyInfo} className="mt-5 mb-2 flex items-center gap-2 text-sm text-[#93C5FD] hover:text-white transition-colors">
          <Info size={16} /> {t("Come funziona la verifica licenza / flotta")}
        </button>
        <button type="button" data-testid="profile-logout-button" onClick={onLogout} className="ff-btn-secondary w-full mt-4 mb-4 text-sm uppercase">{t("Esci dall'account")}</button>
      </SheetContent>
    </Sheet>
  );
};
